import { useEffect, useState } from "react";
import axios from "axios";
import { Package, Users, DollarSign, TrendingUp, TrendingDown, Clock, CheckCircle, ExternalLink, Wallet, Landmark } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh every 5 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, activityRes, ridersRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`),
        axios.get(`${API}/dashboard/recent-activity`),
        axios.get(`${API}/riders`),
      ]);
      setSummary(summaryRes.data);
      setRecentActivity(activityRes.data);
      setRiders(ridersRes.data.filter((r) => r.active));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const metrics = [
    {
      label: "Total Orders",
      value: summary?.total_orders || 0,
      icon: Package,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      label: "Active Riders",
      value: summary?.active_riders || 0,
      icon: Users,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      label: "Charges Pending",
      value: `₹${(summary?.delivery_charges_pending || 0).toFixed(2)}`,
      icon: Clock,
      color: "text-amber-600",
      bg: "bg-amber-50",
      desc: "Delivery fees not yet collected",
    },
    {
      label: "Charges Collected",
      value: `₹${(summary?.delivery_charges_collected || 0).toFixed(2)}`,
      icon: CheckCircle,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      desc: "Delivery fees collected by riders",
    },
    {
      label: "Cash Collected",
      value: `₹${(summary?.cash_collected || 0).toFixed(2)}`,
      icon: DollarSign,
      color: "text-orange-600",
      bg: "bg-orange-50",
      desc: "Cash riders collected from customers",
    },
    {
      label: "Rider Expenses",
      value: `₹${(summary?.cash_spent || 0).toFixed(2)}`,
      icon: TrendingDown,
      color: "text-red-600",
      bg: "bg-red-50",
      desc: "Cash riders spent (gas, etc.)",
    },
    {
      label: "Rider Balance",
      value: `₹${(summary?.rider_balance || 0).toFixed(2)}`,
      icon: Wallet,
      color: "text-violet-600",
      bg: "bg-violet-50",
      desc: "Cash Collected − Expenses",
    },
    {
      label: "Total In Hand",
      value: `₹${(summary?.total_in_hand || 0).toFixed(2)}`,
      icon: Landmark,
      color: "text-slate-800",
      bg: "bg-slate-100",
      desc: "Charges Collected + Cash Collected − Expenses",
      highlight: true,
    },
  ];

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 rounded-md"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-8 relative" data-testid="dashboard-page">
      {/* Background logo watermark */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/3 -translate-y-1/2 pointer-events-none z-0" style={{marginLeft: '32px'}}>
        <img
          src="https://customer-assets.emergentagent.com/job_1a9e1758-603d-4291-9897-b87c468206e6/artifacts/5k450quw_tpl%20logo.jpeg"
          alt=""
          className="w-[500px] opacity-[0.09] select-none"
          draggable="false"
        />
      </div>

      <div className="relative z-10 space-y-8">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="dashboard-title">
          Thawkpalai Delivery Service
        </h1>
        <p className="text-slate-500 mt-2">Overview of today's delivery operations</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="metrics-grid">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div
              key={index}
              className={`border shadow-sm rounded-md p-6 transition-all ${
                metric.highlight
                  ? "bg-slate-900 border-slate-700 col-span-1 md:col-span-2 lg:col-span-4"
                  : "bg-white border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`metric-card-${index}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className={`text-xs font-bold uppercase tracking-wider ${metric.highlight ? "text-slate-400" : "text-slate-500"}`}>
                    {metric.label}
                  </p>
                  <p className={`font-extrabold tracking-tighter mt-2 ${metric.highlight ? "text-4xl text-white" : "text-3xl text-slate-900"}`}>
                    {metric.value}
                  </p>
                  {metric.desc && (
                    <p className={`text-xs mt-1 ${metric.highlight ? "text-slate-500" : "text-slate-400"}`}>{metric.desc}</p>
                  )}
                </div>
                <div className={`${metric.highlight ? "bg-slate-800 text-white" : `${metric.bg} ${metric.color}`} p-3 rounded-md`}>
                  <Icon size={24} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Riders Quick Access */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-md" data-testid="riders-quick-access">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
            Riders Dashboard Quick Access
          </h2>
          <p className="text-sm text-slate-500 mt-1">View individual rider performance</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {riders.length === 0 ? (
              <div className="col-span-full text-center py-8 text-slate-500">
                No active riders found
              </div>
            ) : (
              riders.map((rider) => (
                <button
                  key={rider.id}
                  onClick={() => navigate(`/riders/${rider.id}`)}
                  className="flex items-center gap-3 p-4 border border-slate-200 rounded-md hover:border-orange-500 hover:bg-orange-50 transition-all text-left group"
                  data-testid={`rider-quick-access-${rider.id}`}
                >
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {rider.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {rider.name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{rider.vehicle_number}</p>
                  </div>
                  <ExternalLink 
                    size={18} 
                    className="text-slate-400 group-hover:text-orange-500 flex-shrink-0" 
                  />
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-md" data-testid="recent-activity">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
            Recent Activity
          </h2>
        </div>
        <div className="divide-y divide-slate-100">
          {recentActivity.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No recent activity</div>
          ) : (
            recentActivity.slice(0, 10).map((activity) => (
              <div
                key={activity.id}
                className="p-4 hover:bg-slate-50 transition-colors"
                data-testid="activity-item"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{activity.description}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {format(new Date(activity.timestamp), "MMM dd, yyyy 'at' hh:mm a")}
                    </p>
                  </div>
                  <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-600">
                    {activity.activity_type.replace("_", " ")}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Dashboard;
